import React, { useState } from 'lib/react';
import {
  EVChip,
  EVClear,
  EVFormHelperText,
  EVGrid,
  EVInputLabel,
  EVTextField, EVTypography,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import ChipInput from 'commonComponents/chip-input';
import { EMAIL_REGEX } from 'constants/AppConstants';
import { useSelector, useDispatch } from 'lib/redux';
import clsx from 'clsx';
import { getUserDetails } from 'utils/app.utils';
import { Link } from 'lib/router';
import { CONTACT_INFORMATION_ROUTE } from 'constants/RouteConstants';
import LabelledCheckbox from 'commonComponents/labelled-checkbox';
import { updateInputFieldValue } from 'features/orderPreferences/OrderPreferences.actions';
import { getPropertyValues } from 'features/orderPreferences/OrderPreferences.utils';
import EmailRecipientsStyles from './EmailRecipientsStyles';

function EmailRecipients() {
  const classes = EmailRecipientsStyles();
  const orderPreferences = useSelector(({ userData }) => userData.orderPreferences);
  const inputFieldValues = useSelector(({ orderPreference }) => orderPreference.inputFieldValues);
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const dispatch = useDispatch();
  const emailCC = getPropertyValues(orderPreferences, inputFieldValues, 'emailCC');

  const handleEmailInputChange = (event, email) => {
    // #genai - Ignore absent events while preserving the change-event check.
    if (event?.type === 'change') setInputValue(email);
  };

  const setEmailIDs = (list) => dispatch(updateInputFieldValue('emailCC', 'value', list.join('; ')));

  const handleEmailAdd = (list) => {
    setEmailIDs(list);
    setInputValue('');
  };

  const handleEmailDelete = (list) => setEmailIDs(list);

  const handleClear = (list) => {
    setEmailIDs(list);
    setInputValue('');
  };

  const handleEmailSubAccountsCheckBoxChange = (e) => {
    const { target: { name, checked } } = e;
    dispatch(updateInputFieldValue(name, 'allowSubAccounts', checked));
  };

  /**
   * Render the selected value.
   *
   * @param {T[]} value The `value` provided to the component.
   * @param {function} getTagProps A tag props getter.
   * @returns {ReactNode}
   */
  const renderTags = (values, getProps) => values.map((value, index) => {
    const { className, ...otherProps } = getProps({ index });
    const invalid = !EMAIL_REGEX.test(value);

    return (
      <EVChip
        label={value}
        className={clsx(className, invalid && 'error')}
        deleteIcon={<EVClear className={clsx(classes.tagDeleteIcon, invalid && 'error')} />}
        {...otherProps}
      />
    );
  });

  // #genai - Keep an empty preference mapped to an empty recipient list.
  const emailIDs = emailCC?.value
    ? emailCC.value.split(';').map((value) => value.trim())
    : [];

  const hasInvalidEmailIDs = emailIDs.some((value) => !EMAIL_REGEX.test(value));

  return (
    <EVGrid className={classes.measurementInstructionContainer}>
      <EVTypography className={classes.structureTypeTitle}>{t('order_submitted__additional_recipients')}</EVTypography>
      <EVInputLabel htmlFor="emailRecipientsReports" className={classes.emailRecipientsReportsLabel}>
        {t('finalize_order__email_recipients_reports')}
        <b>{`${getUserDetails()?.userName}. `}</b>
        <Link
          className={classes.editContactInfo}
          to={CONTACT_INFORMATION_ROUTE}
          aria-label="EditContactInfo"
        >
          {t('finalize_order__edit_contact_info')}
        </Link>
      </EVInputLabel>
      <EVGrid container direction="column" style={{ gap: '8px', flexWrap: 'nowrap' }}>
        <EVInputLabel htmlFor="emailRecipientsSendReport" className={classes.emailRecipientsReportsSubLabel}>
          {t('order_preferences__product_selection_email_text')}
        </EVInputLabel>
        <ChipInput
          id="emailIds-input"
          inputValue={inputValue}
          onInputChange={handleEmailInputChange}
          renderInput={(otherProps) => <EVTextField variant="outlined" {...otherProps} />}
          onAddValue={handleEmailAdd}
          onDeleteValue={handleEmailDelete}
          onClear={handleClear}
          classes={{ inputRoot: clsx(classes.emailInputRoot, hasInvalidEmailIDs && 'error'), tag: classes.emailInputTag }}
          className={classes.emailInput}
          value={emailIDs}
          renderTags={renderTags}
        />
        {hasInvalidEmailIDs && (
          <EVFormHelperText className={classes.invalidEmail}>
            {t('finalize_order__invalid_email_address')}
          </EVFormHelperText>
        )}
      </EVGrid>
      <EVGrid className={clsx(classes.measurementInstructionCB, emailCC?.visible === false && 'hide')}>
        <LabelledCheckbox
          checkboxId="emailCC"
          checkBoxName="emailCC"
          handleCheckboxSelectChange={handleEmailSubAccountsCheckBoxChange}
          checked={emailCC?.allowSubAccounts}
          checkboxValue={emailCC?.allowSubAccounts}
          checkboxLabel={t('order_preferences__product_selection_email_CB')}
        />
      </EVGrid>
    </EVGrid>
  );
}

export default EmailRecipients;
